/**
 * "To continue" banner (2026-02) — shows the user's tasks that were
 * silently parked to Waiting when they clocked out, so they can resume
 * any number of them (no single-task limit). Mounted on the dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Play, RotateCcw } from 'lucide-react';
import { attendanceV2API } from '../../lib/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { fmtDateLong } from '../../lib/attendanceV2';

export default function ToContinueBanner() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const [tasks, setTasks] = useState([]);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await attendanceV2API.tasksToContinue(wsId);
      setTasks(res.data.tasks || []);
    } catch { /* banner is best-effort */ }
  }, [wsId]);

  useEffect(() => {
    load();
    window.addEventListener('stellar-clock-changed', load);
    return () => window.removeEventListener('stellar-clock-changed', load);
  }, [load]);

  const resume = async (t) => {
    setBusyId(t.id);
    try {
      await attendanceV2API.resumeTask(wsId, t.id);
      toast.success(`Resumed: ${t.title}`);
      setTasks((p) => p.filter((x) => x.id !== t.id));
      try { window.dispatchEvent(new Event('stellar-clock-changed')); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resume the task, please try again.');
    } finally {
      setBusyId('');
    }
  };

  if (!tasks.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-3 space-y-2" data-testid="to-continue-banner">
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-400">
        <RotateCcw className="h-3.5 w-3.5" />
        To continue — parked when you clocked out
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tasks.map((t) => (
          <div key={t.id}
            className="flex items-center gap-2 rounded-md bg-foreground/[0.04] border border-border/40 pl-2.5 pr-1 py-1"
            data-testid={`to-continue-task-${t.id}`}>
            <span className="text-[12px] truncate max-w-[240px]" title={`Parked ${fmtDateLong(t.parked_date)}`}>
              {t.title}
            </span>
            {t.client_name && <span className="text-[9px] px-1 py-0.5 rounded bg-gold/10 text-gold">{t.client_name}</span>}
            <button
              type="button"
              disabled={busyId === t.id}
              onClick={() => resume(t)}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 font-medium"
              data-testid={`to-continue-resume-${t.id}`}
            >
              <Play className="h-2.5 w-2.5" /> In Progress
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
